import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu, X, ArrowRight, Check, Loader2, CalendarCheck, CheckCircle2,
  Users, FileText, CalendarRange, PhoneCall, Sparkles,
  Mic, Play, Bot, Navigation, ShieldCheck, CreditCard, Wallet,
  MapPin, User, UsersRound, Building2, PhoneIncoming, DollarSign,
  Headset, Wand2, Workflow, Star, MessageSquareText, Mail,
  Megaphone, Wrench, BarChart3, Calendar,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Calendar as CalendarPicker } from "../components/ui/calendar";
import { demoBookingApi, platformCalendarApi } from "../lib/api";
import { toast } from "sonner";
import { usePlatformBranding, resolveBrandAsset } from "../contexts/PlatformBrandingContext";
import { SEO } from "../components/seo/SEO";
import { getPageSeo } from "../lib/seoConfig";
import { organizationSchema, softwareApplicationSchema, websiteSchema } from "../components/seo/schema";
import { trackEvent } from "../lib/analytics";

const LOGO = "/brand-logo.png";
const VOICE_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_7a4723c8-5c31-4d54-8808-431a92eea82c/artifacts/wg0ar1oz_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2001_17_55%20PM.png";
const HANDSFREE_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_7a4723c8-5c31-4d54-8808-431a92eea82c/artifacts/e8efplgj_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2001_45_16%20PM.png";
const CALLING_IMG = "/ai-calling-hub.png";
const HERO_BG = process.env.REACT_APP_BACKEND_URL + "/api/assets/landing-hero-bg";
const HERO_MOBILE_IMG = process.env.REACT_APP_BACKEND_URL + "/api/assets/landing-hero-mobile";
const FINAL_CTA_IMG = "https://images.pexels.com/photos/5366406/pexels-photo-5366406.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const NAVY = "bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]";
const DOTS = {
  backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.06) 1px, transparent 0)",
  backgroundSize: "32px 32px",
};

const btnPrimary =
  "inline-flex items-center justify-center gap-2 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[14px] font-bold px-6 py-3.5 shadow-sm hover:shadow-md active:scale-95 transition-all";
const btnGhostDark =
  "inline-flex items-center justify-center gap-2 rounded-full bg-white/5 text-white text-[14px] font-bold px-6 py-3.5 border border-white/15 hover:bg-white/10 active:scale-95 transition-all";

const HERO_TILES = [
  { label: "CRM", sub: "Voice Commands", icon: Mic, color: "#2563EB" },
  { label: "Dispatching", sub: "& GPS Monitoring", icon: MapPin, color: "#10B981" },
  { label: "AI Marketing", sub: "& Design", icon: Megaphone, color: "#8B5CF6" },
  { label: "AI Receptionist", sub: "Books & Follows Up", icon: Calendar, color: "#F97316" },
  { label: "Equipment", sub: "Diagnostics", icon: Wrench, color: "#EF4444" },
  { label: "Reporting", sub: "Clear Business Insights", icon: BarChart3, color: "#2563EB" },
];

const CRM_FEATURES = [
  { icon: CalendarRange, title: "Dispatch & Scheduling", desc: "Drag-and-drop dispatch board and a live calendar that keeps every truck moving and every slot full." },
  { icon: FileText, title: "Estimates & Invoices", desc: "Build estimates in seconds, convert to invoices, and collect with online pay links — no chasing checks." },
  { icon: Users, title: "CRM & Customer Profiles", desc: "Every customer's jobs, equipment, history, and memberships in one organized record." },
  { icon: PhoneCall, title: "AI Voice Agent", desc: "Automated follow-up calls that text or email the invoice with a pay link and book the next job." },
  { icon: Sparkles, title: "Diagnostic Guru", desc: "AI diagnostic co-pilot trained on your service manuals — walks techs through error codes, symptoms, and flow charts in real time." },
  { icon: Mic, title: "Guru Assistant", desc: "Voice command your back-office — draft an estimate, book a job, or message a tech hands-free while you drive." },
  { icon: Wallet, title: "Payments & Payroll", desc: "Take card payments in the field, track timeclock hours, and run payroll from one platform." },
  { icon: Wand2, title: "AI Email Designer", desc: "Generate on-brand marketing emails with AI-designed images in seconds — no designer needed." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { branding } = usePlatformBranding();
  const navLogoSrc = resolveBrandAsset(branding.sidebarLogoUrl) || LOGO;
  const [menuOpen, setMenuOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openDemo = () => { setMenuOpen(false); setDemoOpen(true); };
  const scrollTo = (id) => { setMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); };

  const seo = getPageSeo("/");
  return (
    <div className="min-h-screen bg-slate-950 text-white antialiased">
      <SEO
        title={seo.title}
        description={seo.description}
        path="/"
        jsonLd={[organizationSchema(), softwareApplicationSchema(), websiteSchema()]}
      />
      <style>{`
        @keyframes lp-fadeup { from { opacity:0; transform: translateY(20px) } to { opacity:1; transform:none } }
        .lp-fadeup { animation: lp-fadeup .7s cubic-bezier(.2,.7,.2,1) both; }
        @keyframes lp-pulse { 0%,100% { opacity:1 } 50% { opacity:.35 } }
        .lp-live { animation: lp-pulse 1.6s ease-in-out infinite; }
      `}</style>

      {/* ===== NAVBAR ===== */}
      <header className={`sticky top-0 z-50 transition-all ${scrolled ? "bg-slate-950/90 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/20" : "bg-slate-950/70 backdrop-blur-xl border-b border-white/5"}`}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[68px] flex items-center justify-between gap-4">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="nav-logo" className="flex items-center gap-2.5 shrink-0">
            <div className="h-[46px] w-[46px] rounded-xl bg-white border border-white/10 shadow-sm flex items-center justify-center overflow-hidden">
              <img src={navLogoSrc} alt="ServiceGuru Pro" className="h-full w-full object-contain" />
            </div>
            <div className="leading-none text-left">
              <div className="font-bold text-[17px] tracking-tight text-white">ServiceGuru Pro</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-0.5">Field Ops Platform</div>
            </div>
          </button>

          <nav className="hidden lg:flex items-center gap-8 text-[14px] font-medium text-slate-300">
            <Link to="/features" data-testid="nav-features" className="hover:text-white transition-colors">Features</Link>
            <Link to="/industries" data-testid="nav-industries" className="hover:text-white transition-colors">Industries</Link>
            <Link to="/solutions" data-testid="nav-solutions" className="hover:text-white transition-colors">Solutions</Link>
            <Link to="/resources" data-testid="nav-resources" className="hover:text-white transition-colors">Resources</Link>
            <Link to="/pricing" data-testid="nav-pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/contact" data-testid="nav-contact" className="hover:text-white transition-colors">Contact</Link>
          </nav>

          <div className="hidden lg:flex items-center gap-2.5">
            <Link to="/login" data-testid="nav-login" className="px-4 py-2 text-[14px] font-semibold text-slate-300 hover:text-white transition-colors">Login</Link>
            <Link to="/signup" data-testid="nav-signup" className={`${btnPrimary} !py-2 !rounded-full`}>Start Free Trial</Link>
          </div>

          <button onClick={() => setMenuOpen((v) => !v)} data-testid="nav-mobile-toggle" className="lg:hidden p-2 rounded-lg border border-white/10 text-white">
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {menuOpen && (
          <div className="lg:hidden border-t border-white/10 bg-slate-950 px-5 py-4 space-y-1.5" data-testid="nav-mobile-menu">
            {[["Features", "/features"], ["Industries", "/industries"], ["Solutions", "/solutions"], ["Resources", "/resources"], ["Pricing", "/pricing"], ["Contact", "/contact"]].map(([l, to]) => (
              <Link key={l} to={to} onClick={() => setMenuOpen(false)} className="block w-full text-left font-medium text-slate-300 py-2">{l}</Link>
            ))}
            <div className="pt-2 grid grid-cols-1 gap-2.5">
              <Link to="/login" data-testid="nav-login-mobile" className={btnGhostDark}>Login</Link>
              <button onClick={openDemo} className={btnGhostDark}>Book a demo</button>
              <Link to="/signup" className={btnPrimary}>Start Free Trial</Link>
            </div>
          </div>
        )}
      </header>

      {/* ===== 1. HERO ===== */}
      <section className="relative overflow-hidden" style={{ backgroundColor: "#0A0F1E" }}>
        {/* Tablet: capped-height background (unchanged from before, now scoped to 768-1023 only) */}
        <div
          className="hidden md:block lg:hidden absolute top-0 left-0 right-0 h-[560px]"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
            maskImage: "linear-gradient(to bottom, black 65%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(to bottom, black 65%, transparent 100%)",
          }}
          data-testid="hero-bg-image-mobile"
        />
        {/* Desktop: unchanged full-section cover background */}
        <div
          className="hidden lg:block absolute inset-0"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
          }}
          data-testid="hero-bg-image"
        />
        <div className="hidden md:block absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,15,30,0.55) 0%, rgba(10,15,30,0.28) 38%, rgba(10,15,30,0) 62%)" }} />
        <div className="hidden md:block absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0A0F1E] to-transparent" />

        {/* Tablet/Desktop content — unchanged, hidden below 768px */}
        <div className="hidden md:block relative max-w-[1280px] mx-auto px-6 pt-16 sm:pt-20 pb-10">
          <div className="lp-fadeup max-w-xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-[#0A0F1E]/60 px-3.5 py-1.5 text-[12px] font-bold uppercase tracking-wide text-amber-400">
              <Star className="h-3.5 w-3.5 fill-current" /> All-in-One Field Service Platform
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white" data-testid="hero-headline">
              Run a Smarter<br />
              <span className="text-blue-400">Field Service</span><br />
              Business.
            </h1>
            <p className="mt-6 max-w-lg text-base sm:text-lg text-slate-300 leading-relaxed" data-testid="hero-subheadline">
              CRM, dispatching, AI marketing, AI receptionist, equipment diagnostics and reporting — all in one easy-to-use platform. Hands-free with voice commands so your team can get more done, every day.
            </p>
            <div className="mt-9 flex flex-col gap-3.5 sm:flex-row">
              <Link to="/signup" data-testid="hero-cta-trial" className={btnPrimary}>Start Free Trial <ArrowRight className="h-4 w-4" /></Link>
              <button onClick={openDemo} data-testid="hero-cta-demo" className={btnGhostDark}><Play className="h-4 w-4 fill-current" /> Book a Demo</button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-slate-300">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-blue-400" /> No credit card required</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-blue-400" /> 14-day free trial</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-blue-400" /> Setup in minutes</span>
            </div>
          </div>

          {/* Feature tile strip */}
          <div className="mt-16 sm:mt-20 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 lp-fadeup" style={{ animationDelay: ".12s" }} data-testid="hero-feature-tiles">
            {HERO_TILES.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.label}
                  data-testid={`hero-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-2xl border border-white/10 bg-[#0A0F1E]/70 backdrop-blur-md px-3.5 py-4 flex flex-col items-center text-center gap-2.5"
                >
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: t.color }}>
                    <Icon className="h-5 w-5 text-white" strokeWidth={2.2} />
                  </div>
                  <div>
                    <div className="text-[13.5px] font-bold text-white leading-tight">{t.label}</div>
                    <div className="text-[11.5px] text-slate-400 leading-tight mt-0.5">{t.sub}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile-only hero — below 768px */}
        <div className="md:hidden relative">
          <img
            src={HERO_MOBILE_IMG}
            alt="ServiceGuru Pro — run a smarter field service business"
            className="w-full h-auto block"
            data-testid="hero-mobile-image"
          />
          <div className="px-6 py-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/signup" data-testid="hero-cta-trial-mobile" className={`${btnPrimary} w-full sm:w-auto justify-center`}>Start Free Trial <ArrowRight className="h-4 w-4" /></Link>
            <button onClick={openDemo} data-testid="hero-cta-demo-mobile" className={`${btnGhostDark} w-full sm:w-auto justify-center`}><Play className="h-4 w-4 fill-current" /> Book a Demo</button>
          </div>
        </div>
      </section>


      {/* ===== 2. BUILT FOR ANY SIZE ===== */}
      <section className="bg-slate-950 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="mx-auto mb-14 max-w-2xl text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="size-headline">
              From Solo Operators to Growing Teams.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">
              ServiceGuru Pro scales from one tech to twenty without adding headcount — the same AI assistant keeps every size of operation moving.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            <SizePanel icon={User} iconCount={1} title="Solo Operator" desc="One tech, running the whole business from the truck." />
            <SizePanel icon={UsersRound} iconCount={3} title="Small Team" desc="A few techs, one shared schedule, zero double-booking." />
            <SizePanel icon={Building2} iconCount={4} title="Larger Dispatch" desc="Full dispatch board, AI handling the overflow calls." />
          </div>
        </div>
      </section>

      {/* ===== 3. AI VOICE ASSISTANT ===== */}
      <section id="ai-voice" className="leading-[0]">
        <img src="/ai-voice-assistant-crm.png" alt="Talk to Your CRM AI, Get More Done — voice-create estimates, emails, tasks, and calendar events inside ServiceGuru Pro" className="w-full block" data-testid="voice-image" loading="lazy" decoding="async" />
      </section>

      {/* ===== 4. HANDS-FREE OPERATIONS ===== */}
      <section className="bg-slate-900/40 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="mb-14 max-w-2xl">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="handsfree-headline">
              Work With Your Hands. Let AI Handle the Rest.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">
              Whether on a roof, under a sink, or driving between jobs, the AI logs updates, sets reminders, and keeps the schedule on track.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/40">
              <img src="https://i.imgur.com/dD1ZXDo.jpeg" alt="Plumber talking hands-free to the Guru AI assistant from his service van" className="h-[360px] w-full object-cover sm:h-[420px]" data-testid="handsfree-image" loading="lazy" decoding="async" />
            </div>
            <DashboardMock />
          </div>
        </div>
      </section>

      {/* ===== 5. AI CALLING ===== */}
      <section id="ai-calling" className="bg-slate-900/40 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="calling-headline">
              AI That Answers Calls, Sends Estimates, and Chases Invoices.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">
              No matter the size of your company, our AI handles inbound calls, sends estimates, makes outbound sales calls, and follows up on unpaid invoices — so you get paid faster and spend less time chasing.
            </p>
          </div>
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
            <div className="grid gap-5">
              {[
                { icon: PhoneIncoming, title: "Inbound Call Handling", desc: "Every call answered, every time — no lead falls through." },
                { icon: FileText, title: "Automated Estimate Follow-Up", desc: "AI calls to close the loop after every estimate sent." },
                { icon: DollarSign, title: "Invoice Collection Calls", desc: "Friendly, persistent reminders until invoices get paid." },
              ].map((f) => (
                <div key={f.title} className="flex items-start gap-4 rounded-2xl border border-white/10 bg-slate-900/60 p-5" data-testid={`calling-feature-${f.title.split(" ")[0].toLowerCase()}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-500/15">
                    <f.icon className="h-5 w-5 text-blue-400" />
                  </span>
                  <div>
                    <div className="text-[15px] font-bold text-white">{f.title}</div>
                    <div className="mt-1 text-[13.5px] text-slate-400">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/40">
              <img src={CALLING_IMG} alt="Technician working while ServiceGuru Pro's AI voice agent handles an incoming call, sends an estimate, makes an outbound sales call, and follows up on an invoice" className="w-full object-cover" data-testid="calling-image" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== 5b. REPUTATION MANAGEMENT (part 1 — detection & trust) ===== */}
      <section id="reputation-management" className="bg-slate-950 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-amber-300 bg-amber-500/10 border border-amber-500/20 px-3 py-1 rounded-full mb-4">
              <Star className="h-3.5 w-3.5 fill-amber-300" /> Reputation Management
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="reputation-headline">
              Turn Happy Customers Into 5-Star Reviews.
            </h2>
            <p className="mt-3 text-[15px] font-semibold text-blue-400">
              Protect Your Reputation. Improve Your Ratings. Win More Customers.
            </p>
            <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">
              Your online reputation can make or break the next sale. When potential customers need an HVAC company,
              plumber, electrician, roofer, or contractor, one of the first things they do is check reviews — a strong
              reputation builds trust before they ever call you.
            </p>
            <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">
              Most businesses don't know a customer is unhappy until it's too late. ServiceGuru Pro helps you stay
              connected after every job through text messaging, email, and AI-powered calls that gather feedback and
              flag dissatisfaction immediately — so you hear about a problem directly from your customer, not from a
              public review.
            </p>
            <ul className="mt-7 space-y-3.5">
              {[
                "Get notified the moment a customer's feedback signals a problem",
                "Reach out and resolve it before it ever becomes a public review",
                "Stay connected automatically — no manual follow-up required",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px] text-slate-200">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500/15">
                    <CheckCircle2 className="h-3.5 w-3.5 text-blue-400" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
            <Link to="/features/reputation-management" data-testid="reputation-learn-more" className="mt-7 inline-flex items-center gap-1.5 text-[14px] font-semibold text-blue-400 hover:text-blue-300 transition-colors">
              See how it works <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/50">
            <img src="https://i.imgur.com/LEt8ZaO.png" alt="HVAC technician smiling, with a before/after rating comparison showing 2.1 stars improved to 4.9 stars using ServiceGuru Pro's Reputation Management" className="w-full object-cover" data-testid="reputation-image" loading="lazy" decoding="async" />
          </div>
        </div>
      </section>

      {/* ===== 5c. REPUTATION MANAGEMENT (part 2 — encourage more reviews) ===== */}
      <section className="bg-slate-900/40 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="mx-auto mb-14 max-w-2xl text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="reputation-encourage-headline">
              Catch Problems Early. Reward Great Service With More Reviews.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">
              Happy customers are often your best source of new business — but they don't always think to leave a
              review. ServiceGuru Pro keeps your business top of mind after a great job, making it easy to turn
              satisfied customers into genuine 5-star reviews instead of hoping they remember.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: MessageSquareText, title: "Text Messaging", desc: "Friendly review requests sent the moment a job wraps up, while the experience is still fresh." },
              { icon: Mail, title: "Email", desc: "Branded review-request emails that route happy customers straight to your public review link." },
              { icon: PhoneCall, title: "AI-Powered Calls", desc: "AI-powered calls that check in on satisfaction and gather feedback without you lifting a finger." },
            ].map((f) => (
              <div key={f.title} className="rounded-2xl border border-white/10 bg-slate-900/60 p-6 text-center" data-testid={`reputation-channel-${f.title.split(" ")[0].toLowerCase()}`}>
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500/15 mb-4">
                  <f.icon className="h-5.5 w-5.5 text-blue-400" />
                </span>
                <div className="text-[15px] font-bold text-white">{f.title}</div>
                <div className="mt-2 text-[13.5px] text-slate-400 leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 6. CRM FEATURES GRID ===== */}
      <section id="features" className="bg-white py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="mb-14 max-w-2xl">
            <div className="text-[12px] font-bold uppercase tracking-wide text-blue-600" data-testid="crm-grid-eyebrow">Everything In One Platform</div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="crm-grid-headline">
              One system for every job, start to finish
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
              Stop stitching together six different apps. ServiceGuru Pro runs your office, your dispatch board, your field crews, and your AI sales agent.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {CRM_FEATURES.map((f) => (
              <div key={f.title} className="rounded-2xl border border-slate-200 p-6 transition-shadow hover:shadow-lg" data-testid={`crm-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <f.icon className="h-5 w-5" />
                </span>
                <div className="mt-4 text-[15px] font-bold text-slate-900">{f.title}</div>
                <div className="mt-1.5 text-[13.5px] leading-relaxed text-slate-500">{f.desc}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/features" onClick={() => setTimeout(() => window.scrollTo(0, 0), 0)} data-testid="landing-see-all-features-link" className="inline-flex items-center gap-1.5 text-blue-600 font-semibold text-[14px] hover:text-blue-700 transition-colors">
              See all 25 features <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link to="/industries" data-testid="landing-see-industries-link" className="inline-flex items-center gap-1.5 text-slate-500 font-semibold text-[14px] hover:text-slate-700 transition-colors">
              Built for your trade <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>
      <section className="border-y border-white/10 bg-slate-900/60 py-8">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          {[
            { icon: ShieldCheck, text: "Built for field service businesses" },
            { icon: CreditCard, text: "Stripe-secured payments" },
            { icon: Bot, text: "AI-powered, human-controlled" },
            { icon: MapPin, text: "Florida-based, US support" },
          ].map((it) => (
            <span key={it.text} className="flex items-center gap-2 text-[13px] font-semibold text-slate-400" data-testid={`trust-item-${it.text.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}>
              <it.icon className="h-4 w-4 text-blue-400" /> {it.text}
            </span>
          ))}
        </div>
      </section>

      {/* ===== 8. FINAL CTA ===== */}
      <section className="relative overflow-hidden py-24 sm:py-32">
        <img src={FINAL_CTA_IMG} alt="Aerial view of a residential neighborhood at golden hour with a service van on the street" className="absolute inset-0 h-full w-full object-cover" data-testid="final-cta-image" loading="lazy" decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/85 to-slate-950/60" />
        <div className="relative max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight" data-testid="final-cta-headline">
            Stop Juggling. Start Growing.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-300" data-testid="final-cta-subheadline">
            Get a 14 day free trial when you sign up today. No credit card required.
          </p>
          <Link to="/signup" data-testid="final-cta-trial" className={`${btnPrimary} mt-9 inline-flex px-9 py-4 text-[16px]`}>
            Get Started Free <ArrowRight className="h-4.5 w-4.5" />
          </Link>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-[#0B1220] text-slate-400 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5">
              <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center overflow-hidden"><img src={LOGO} alt="ServiceGuru Pro" className="h-full w-full object-contain" /></div>
              <span className="text-white font-bold text-[16px] tracking-tight">ServiceGuru Pro</span>
            </div>
            <p className="mt-3 text-[13px] max-w-[230px] leading-relaxed">The all-in-one platform for high-performance home-service businesses.</p>
          </div>
          <FooterCol title="Product" links={[["AI Assistant", () => scrollTo("ai-voice")], ["Features Overview", () => navigate("/features")], ["Pricing", () => navigate("/pricing")]]} />
          <FooterCol title="Solutions" links={[["Industries We Serve", () => navigate("/industries")], ["Solutions Overview", () => navigate("/solutions")], ["Resources", () => navigate("/resources")]]} />
          <FooterCol title="Company" links={[["Start free trial", () => navigate("/signup")], ["Login", () => navigate("/login")], ["Book a demo", openDemo], ["Become an Affiliate", () => navigate("/become-an-affiliate")]]} />
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[12.5px] text-slate-500">
            <span>© {new Date().getFullYear()} ServiceGuru Pro. All rights reserved.</span>
            <div className="flex items-center gap-4">
              <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>

      <BookDemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}

function DashboardMock() {
  const rows = [
    { label: "Job J-1042 · Water heater repair", tag: "Scheduled", color: "bg-blue-500/15 text-blue-300" },
    { label: "Task · Order replacement part", tag: "Added", color: "bg-amber-500/15 text-amber-300" },
    { label: "Event · Callback @ 3:00 PM", tag: "Synced", color: "bg-emerald-500/15 text-emerald-300" },
  ];
  return (
    <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-2xl shadow-black/40" data-testid="dashboard-mock">
      <div className="mb-5 flex items-center justify-between">
        <span className="text-[12px] font-bold uppercase tracking-wide text-slate-500">Live Dashboard</span>
        <span className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-400"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Auto-updating</span>
      </div>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-slate-950/60 px-4 py-3.5">
            <span className="text-[13px] font-medium text-slate-200">{r.label}</span>
            <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-bold ${r.color}`}>{r.tag}</span>
          </div>
        ))}
      </div>
      <div className="mt-5 h-2 w-full overflow-hidden rounded-full bg-slate-800">
        <div className="h-full w-2/3 rounded-full bg-gradient-to-r from-blue-500 to-blue-400" />
      </div>
      <div className="mt-2 text-[11px] text-slate-500">Schedule 67% confirmed for today</div>
    </div>
  );
}

function SizePanel({ icon: Icon, iconCount, title, desc }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-7" data-testid={`size-panel-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="mb-6 flex items-center gap-1.5">
        {Array.from({ length: iconCount }).map((_, i) => (
          <span key={i} className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-500/15">
            <Icon className="h-4.5 w-4.5 text-blue-400" />
          </span>
        ))}
      </div>
      <div className="text-[17px] font-bold text-white">{title}</div>
      <div className="mt-1.5 text-[13.5px] leading-relaxed text-slate-400">{desc}</div>
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{title}</div>
      <ul className="mt-3 space-y-2.5">
        {links.map(([label, fn]) => (
          <li key={label}><button onClick={fn} className="text-[13.5px] hover:text-white transition-colors">{label}</button></li>
        ))}
      </ul>
    </div>
  );
}

function BookDemoDialog({ open, onOpenChange }) {
  const [config, setConfig] = useState(null);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState("");
  const [dateSlots, setDateSlots] = useState([]);
  const [loadingDateSlots, setLoadingDateSlots] = useState(false);
  const [step, setStep] = useState("pick"); // pick -> details -> done
  const [f, setF] = useState({ name: "", email: "", note: "" });
  const [busy, setBusy] = useState(false);

  const dateIso = (d) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "");

  const loadSlotsForDate = async (d) => {
    if (!d) { setDateSlots([]); return; }
    setLoadingDateSlots(true);
    try {
      const res = await platformCalendarApi.availableSlots(dateIso(d));
      setDateSlots(res?.slots || []);
    } catch {
      setDateSlots([]);
    } finally {
      setLoadingDateSlots(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoadingSlots(true);
    (async () => {
      try {
        const [cfg, slots] = await Promise.all([
          demoBookingApi.publicConfig(),
          demoBookingApi.publicSlots(dateIso(new Date()), 45),
        ]);
        if (!active) return;
        setConfig(cfg);
        setSlotsByDate(slots);
        const firstAvail = Object.keys(slots).sort().find((d) => (slots[d] || []).length > 0);
        if (firstAvail) {
          const d = new Date(`${firstAvail}T00:00:00`);
          setSelectedDate(d);
          loadSlotsForDate(d);
        }
      } catch {
        toast.error("Could not load availability. Please try again shortly.");
      } finally {
        if (active) setLoadingSlots(false);
      }
    })();
    return () => { active = false; };
  }, [open]);

  // The actual bookable times for the selected date always come live from the
  // availability engine (/api/calendar/available-slots) — slotsByDate above is
  // only used to grey out days with no availability at all on the calendar.
  const timesForSelected = dateSlots;

  const fmtTime = (t) => {
    const [h, m] = t.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
  };
  const fmtDay = (d) => d?.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  const pickTime = (t) => { setSelectedTime(t); setStep("details"); };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) { toast.error("Please add your name and email."); return; }
    setBusy(true);
    try {
      await demoBookingApi.book({ name: f.name, email: f.email, note: f.note, date: dateIso(selectedDate), time: selectedTime });
      setStep("done");
      trackEvent("generate_lead", { lead_type: "demo_booking" });
    } catch (err) {
      const status = err?.response?.status;
      toast.error(err?.response?.data?.detail || "Could not book that slot. Please try again.");
      if (status === 409) {
        setStep("pick");
        setSelectedTime("");
        demoBookingApi.publicSlots(dateIso(new Date()), 45).then(setSlotsByDate).catch(() => {});
        loadSlotsForDate(selectedDate);
      }
    } finally { setBusy(false); }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => { setStep("pick"); setSelectedTime(""); setF({ name: "", email: "", note: "" }); }, 200);
  };

  const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 placeholder:text-slate-400";
  const labelCls = "block text-[12px] font-semibold text-slate-700 mb-1.5";

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0 overflow-hidden" data-testid="book-demo-dialog">
        <DialogHeader className={`${NAVY} text-white p-6 space-y-1.5 text-left`}>
          <DialogTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
            <CalendarCheck className="h-5 w-5 text-amber-400" /> {config?.popupTitle || "Book a Demo"}
          </DialogTitle>
          <DialogDescription className="text-slate-300 text-[13.5px]">
            {config?.popupBody || "See ServiceGuru Pro tailored to your trade."}
          </DialogDescription>
        </DialogHeader>

        {step === "done" ? (
          <div className="p-8 text-center" data-testid="book-demo-success">
            <div className="mx-auto h-14 w-14 rounded-full bg-blue-50 text-[#2563EB] flex items-center justify-center"><Check className="h-7 w-7" /></div>
            <h3 className="mt-4 text-xl font-bold tracking-tight text-slate-900">You're booked!</h3>
            <p className="mt-2 text-slate-600 text-[14px]" data-testid="book-demo-confirmation-details">
              {fmtDay(selectedDate)} at {fmtTime(selectedTime)}{config?.timezone ? ` (${config.timezone})` : ""}
            </p>
            <p className="mt-1 text-slate-500 text-[13px]">We've got your details, {f.name.split(" ")[0]} — talk soon!</p>
            <button onClick={close} data-testid="book-demo-done" className={`${btnPrimary} mt-6`}>Done</button>
          </div>
        ) : loadingSlots ? (
          <div className="p-16 flex items-center justify-center bg-white"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
        ) : step === "pick" ? (
          <div className="bg-white text-slate-900 grid sm:grid-cols-2">
            <div className="p-3 border-b sm:border-b-0 sm:border-r border-slate-100 flex justify-center">
              <CalendarPicker
                mode="single"
                selected={selectedDate}
                onSelect={(d) => { setSelectedDate(d); setSelectedTime(""); loadSlotsForDate(d); }}
                disabled={(d) => {
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const iso = dateIso(d);
                  return d < today || !((slotsByDate[iso] || []).length > 0);
                }}
                data-testid="book-demo-calendar"
              />
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              <div className="text-[12.5px] font-semibold text-slate-500 mb-2">{selectedDate ? fmtDay(selectedDate) : "Pick a date"}</div>
              {loadingDateSlots ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div>
              ) : timesForSelected.length === 0 ? (
                <div className="text-[13px] text-slate-400 italic py-6 text-center" data-testid="book-demo-no-slots">No times available this day.</div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {timesForSelected.map((t) => (
                    <button key={t} type="button" onClick={() => pickTime(t)} data-testid={`book-demo-slot-${t}`} className="rounded-lg border border-slate-200 hover:border-blue-400 hover:bg-blue-50 text-[13px] font-medium text-slate-700 py-2 transition-colors">
                      {fmtTime(t)}
                    </button>
                  ))}
                </div>
              )}
              {config?.timezone && <div className="text-[11px] text-slate-400 mt-3">Times shown in {config.timezone}</div>}
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4 bg-white text-slate-900">
            <button type="button" onClick={() => setStep("pick")} data-testid="book-demo-back" className="text-[12.5px] text-blue-600 font-semibold hover:underline">&larr; Change time</button>
            <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2.5 text-[13.5px] text-blue-800 font-medium flex items-center gap-2">
              <CalendarCheck className="h-4 w-4" /> {fmtDay(selectedDate)} at {fmtTime(selectedTime)}
            </div>
            <div><label className={labelCls}>Full name *</label><input required data-testid="demo-name" value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} className={inputCls} placeholder="Jordan Smith" /></div>
            <div><label className={labelCls}>Email *</label><input required type="email" data-testid="demo-email" value={f.email} onChange={(e) => setF((s) => ({ ...s, email: e.target.value }))} className={inputCls} placeholder="you@company.com" /></div>
            <div><label className={labelCls}>Anything you'd like us to know? (optional)</label><textarea data-testid="demo-notes" value={f.note} onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} rows={3} className={inputCls} placeholder="GPS tracking, dispatch, AI follow-up calls…" /></div>
            <button type="submit" disabled={busy} data-testid="demo-submit" className={`${btnPrimary} w-full`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />} Confirm booking
            </button>
            <p className="text-[11px] text-slate-400 text-center">By booking, you agree to be contacted about ServiceGuru Pro.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
